import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

export default function AboutPage() {
  const { user } = useAuth();

  // Note to Developer form state
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submitNote = async (e) => {
    e.preventDefault();
    setError("");
    setSent(false);

    if (!user) {
      setError("Please sign in first.");
      return;
    }
    if (!subject.trim() || !message.trim()) {
      setError("Please fill subject and message.");
      return;
    }

    try {
      setBusy(true);
      await addDoc(collection(db, "devInbox"), {
        subject: subject.trim(),
        message: message.trim(),
        fromUid: user.uid,
        fromName: user.displayName || "",
        fromEmail: user.email || "",
        createdAt: serverTimestamp(),
        resolved: false,
        meta: {
          userAgent: navigator.userAgent,
          timestamp: Date.now(),
        },
      });
      setSubject("");
      setMessage("");
      setSent(true);
    } catch (e) {
      setError(e?.message || "Could not send note");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h2 className="text-3xl font-semibold text-center text-gray-800 mb-6">About</h2>

      {/* Warning banner */}
      <div className="mb-6 p-4 rounded border bg-yellow-50 text-gray-800">
        ⚠️ This app is for testing purposes only and is intended for use by <strong>Thomas Hill Travel Ltd</strong> and authorised users.
      </div>

      {/* Overview */}
      <section className="space-y-4 mb-6 text-lg text-gray-800">
        <p>
          This application is built using React and Tailwind CSS, aiming to streamline transport
          management so managers can focus on core responsibilities.
        </p>
        <p>
          Please do not use this code for any other purpose without permission from Thomas Hill
          Travel Ltd. This app is not intended for production use.
        </p>
        <p>
          The application provides tools for quick access to important documents, compliance checks,
          and fleet management features for HGV/PSV/Van operations.
        </p>
      </section>

      {/* Disclaimer */}
      <section className="mb-6">
        <h3 className="text-xl font-semibold">Disclaimer</h3>
        <p className="text-lg mt-2 text-gray-800">
          All documents and policies provided within this application should be reviewed by a
          qualified legal professional before use. Thomas Hill Travel Ltd. does not assume any
          responsibility for the accuracy, completeness, or applicability of any documents or
          policies. Legal counsel should be consulted to ensure compliance with relevant laws and
          regulations.
        </p>
      </section>

      {/* EULA */}
      <section className="mb-6">
        <h3 className="text-xl font-semibold">End User License Agreement (EULA)</h3>
        <div className="bg-gray-100 p-4 rounded-md mt-2 text-gray-800">
          <p className="text-lg font-semibold">Thomas Hill Travel Ltd. — End User License Agreement</p>
          <p className="text-lg mt-2">
            By using this application, you agree to the terms and conditions set forth in this End
            User License Agreement (EULA). This EULA governs your use of the application developed by
            Thomas Hill Travel Ltd.
          </p>
          <p className="text-lg mt-2">
            <strong>License Grant:</strong> Thomas Hill Travel Ltd. grants you a non-transferable,
            non-exclusive license to use this application solely for internal business operations
            and testing purposes within your organization.
          </p>
          <p className="text-lg mt-2">
            <strong>Prohibited Uses:</strong> You may not distribute, sell, rent, lease, sublicense,
            or otherwise transfer the application or any part of it to any third party. You may not
            reverse engineer, decompile, or disassemble the application or its components.
          </p>
          <p className="text-lg mt-2">
            <strong>Limitation of Liability:</strong> Thomas Hill Travel Ltd. shall not be liable for
            any damages, including but not limited to indirect, incidental, special, or
            consequential damages arising from the use or inability to use this application.
          </p>
          <p className="text-lg mt-2">
            <strong>Termination:</strong> This license will terminate automatically if you fail to
            comply with any of the terms of this EULA. Upon termination, you must immediately cease
            all use of the application and destroy all copies of it.
          </p>
          <p className="text-lg mt-2">
            <strong>Governing Law:</strong> This EULA is governed by the laws of the United Kingdom.
            Any legal disputes will be resolved in the appropriate courts in the United Kingdom.
          </p>
        </div>
      </section>

      {/* Instructions */}
      <section className="mb-8">
        <h3 className="text-xl font-semibold">📖 Instructions for Users</h3>
        <p className="text-lg mt-2 text-gray-800">
          Documents and policies are templates. You can use them as-is, however if amendments are
          required, click <em>Export to Word</em> on the document page to edit and customise as you
          see fit.
        </p>
      </section>

      {/* Feedback blurb ABOVE the Note to Developer box */}
      <section className="mb-3 p-3 rounded border bg-blue-50 text-blue-900">
        <p className="text-sm">
          <strong>Feedback welcome:</strong> This section is designed for suggestions on what to add, remove, or adjust in the app. 
          Share feature ideas, layout tweaks, missing documents, or anything that would make your day-to-day easier.
        </p>
      </section>

      {/* Note to Developer (writes to Firestore devInbox) */}
      <section className="border rounded p-4 bg-white">
        <h3 className="text-xl font-semibold mb-2">Note to Developer</h3>
        <form onSubmit={submitNote} className="space-y-3">
          <input
            className="w-full p-3 border rounded"
            placeholder="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        
          <textarea
            className="w-full p-3 border rounded h-32"
            placeholder="Write your note here… (bugs, requests, wording changes, new docs, etc.)"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          {sent && <div className="text-green-700 text-sm">Note sent to developer. Thanks!</div>}
          {error && <div className="text-red-600 text-sm">{error}</div>}
          <button
            type="submit"
            disabled={busy}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60"
          >
            {busy ? "Sending…" : "Send Note"}
          </button>
        </form>
        <p className="mt-2 text-xs text-gray-600">Only the Developer can read these notes.</p>
      </section>
    </div>
  );
}
// Note: Ensure you have the necessary Firebase setup and AuthContext in place for this component to function correctly.
